import React, { useContext, useState } from "react";
import DraftStateContext from "../../contexts/draftStateContext";
import useDraftSettings from "../../hooks/queryResults/draft/useDraftSettings";
import useDraftMutations from "../../hooks/queryResults/draft/useDraftMutations";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useTeams from "../../hooks/queryResults/useTeams";
import useCurrentUser from "../../hooks/useCurrentUser";
import Util from "../../util/util";
import TeamLogo from "../team/TeamLogo";
import Loading from "../ui/Loading";
import styled from "styled-components";

const StyledContainerDiv = styled.div`
  & > h2 {
    display: flex;
    align-items: center;
    height: 2.5rem;
    padding: 0 0.5rem;
    margin-block-start: 0.25rem;
    margin-block-end: 0.25rem;
  }

  & > h2 > button {
    margin-left: 1rem;
  }
`;

export default function ChooseSpots() {
  const currentUser = useCurrentUser();
  const draftState = useContext(DraftStateContext);
  const { draftSettingsData, draftSettingsQuery } = useDraftSettings();
  const { teams, teamsQuery } = useTeams();
  const [tempChoice, setTempChoice] = useState(null);
  const { chooseSpotMutation } = useDraftMutations();

  const isLoading = useIsLoading(
    draftSettingsData,
    draftSettingsQuery,
    teams,
    teamsQuery
  );

  if (isLoading) return <Loading />;

  const { spots } = draftState;
  const { draftOrder } = draftSettingsData.settings;

  /** @type {?string} teamIdChoosingNow */
  let teamIdChoosingNow = null;
  for (const teamId of draftOrder) {
    if (!spots.includes(teamId)) teamIdChoosingNow = teamId;
    if (teamIdChoosingNow) break;
  }

  /** @type {?Team} teamChoosingNow */
  let teamChoosingNow = null;
  if (teamIdChoosingNow) teamChoosingNow = teams.getTeamById(teamIdChoosingNow);

  let canChoose = false;
  if (teamIdChoosingNow && teamChoosingNow) {
    canChoose =
      currentUser.isAdmin() ||
      Util.hasDraftManagerRole(currentUser, teamChoosingNow);
  }

  const chooseSpot = (spotNum) => {
    if (!canChoose) return;

    const spotAlreadyChosen = !!spots[spotNum - 1];
    if (spotAlreadyChosen) return;

    console.log(`${teamChoosingNow.fullName} chose spot ${spotNum}`);
    setTempChoice(spotNum);
  };

  const confirmSpot = () => {
    if (!(canChoose && tempChoice)) return;
    const spotNum = tempChoice;
    const spotAlreadyChosen = !!spots[spotNum - 1];
    if (spotAlreadyChosen) return;

    console.log(`${teamChoosingNow.fullName} confirmed spot ${spotNum}`);

    chooseSpotMutation.mutate({ spotNum, team: teamIdChoosingNow });

    setTempChoice(null);
  };

  let header = null;
  if (canChoose && teamChoosingNow) {
    if (tempChoice) {
      header = (
        <h2>
          {teamChoosingNow.fullName} - Confirm draft spot #{tempChoice}{" "}
          <button className={"btn"} onClick={confirmSpot}>
            Confirm
          </button>{" "}
          <button
            className={"btn btn-cancel"}
            onClick={() => setTempChoice(null)}
          >
            Cancel
          </button>
        </h2>
      );
    } else {
      header = (
        <h2>{teamChoosingNow.fullName} - Choose your draft spot NOW!</h2>
      );
    }
  } else if (teamChoosingNow) {
    header = <h2>{teamChoosingNow.fullName} - Choosing draft spot...</h2>;
  }

  let imgSize = 44;

  return (
    <StyledContainerDiv>
      {header}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "0.5rem",
        }}
      >
        {draftState.spots.map((teamId, i) => {
          const spotNum = i + 1;
          let show = spotNum;
          let team = null;
          let opacity = 1;
          if (tempChoice === spotNum) {
            teamId = teamIdChoosingNow;
            opacity = 0.5;
          }
          let color = "#aaa";

          if (teamId) {
            team = teams.getTeamById(teamId);
            color = "white";
            show = (
              <TeamLogo
                team={team}
                size={"lg"}
                styleProps={{
                  width: imgSize + "px",
                  height: imgSize + "px",
                  imageRendering: "crisp-edges",
                  margin: 0,
                  padding: 0,
                  opacity,
                }}
              />
            );
          }

          return (
            <div
              key={i}
              style={{
                width: imgSize + "px",
                height: imgSize + "px",
                color,
                backgroundColor: "white",
                border: `2px solid ${color}`,
                fontSize: "1.75rem",
                verticalAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
              onClick={() => chooseSpot(spotNum)}
            >
              <div>{show}</div>
            </div>
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "0.5rem",
        }}
      >
        {draftSettingsData?.settings.draftOrder.map((teamId, i) => {
          const team = teams.getTeamById(teamId);
          let teamImgSize = imgSize;
          const alreadyChoseSpot = spots.includes(teamId);
          let opacity = alreadyChoseSpot ? 0.25 : 0.5;
          if (teamId === teamIdChoosingNow) {
            opacity = 1;
            teamImgSize = imgSize * 1.5;
          }

          return (
            <TeamLogo
              key={i}
              team={team}
              size={"lg"}
              styleProps={{
                width: teamImgSize + "px",
                height: teamImgSize + "px",
                imageRendering: "crisp-edges",
                margin: 0,
                padding: 0,
                opacity,
              }}
            />
          );
        })}
      </div>
    </StyledContainerDiv>
  );
}
